import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ForexManagement from "@/components/ForexManagement";
import PackageManagement from "@/components/PackageManagement";
import Seo from "@/components/Seo";
import { organizationJsonLd, websiteJsonLd } from "@/lib/seo";

export default function SimpleDashboard() {
  const { user, isLoading, logout } = useAuth();

  // Show loading state while user data is being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Seo
          title="Dashboard | Travel Wings Global"
          description="Internal dashboard for Travel Wings Global team."
          path="/dashboard-simple"
          structuredData={[organizationJsonLd, websiteJsonLd]}
          noindex
        />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    console.warn('SimpleDashboard: No user found, redirecting to login');
    window.location.href = '/login';
    return null;
  }

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Role-based variables (stable)
  const isAdmin = user.role === 'admin' || user.role === 'Admin';

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Dashboard | Travel Wings Global"
        description="Internal dashboard for Travel Wings Global team."
        path="/dashboard-simple"
        structuredData={[organizationJsonLd, websiteJsonLd]}
        noindex
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo.png" 
                  alt="Travel Wings Logo" 
                  className="h-8 w-auto"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Travel Wings Dashboard</h1>
                  <p className="text-sm text-gray-500">Welcome back, {user.username || user.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" className="text-xs px-2 py-1">
                {user.role}
              </Badge>
              <Button onClick={handleLogout} variant="outline" size="sm" className="text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="forex">Forex Management</TabsTrigger>
            <TabsTrigger value="packages">Package Management</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Welcome Card */}
              <Card className="col-span-full">
                <CardHeader>
                  <CardTitle className="text-xl">Welcome to Travel Wings Dashboard!</CardTitle>
                  <CardDescription>Your complete travel management interface</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h3 className="font-semibold text-green-800">Authentication</h3>
                      <p className="text-sm text-green-700">Successfully authenticated as {user.role}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h3 className="font-semibold text-blue-800">User Profile</h3>
                      <p className="text-sm text-blue-700">{user.email}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Travel Packages</CardTitle>
                  <CardDescription>Package management overview</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">12</div>
                  <p className="text-sm text-gray-600">Active packages</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>Current team size</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">15</div>
                  <p className="text-sm text-gray-600">Active members</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Currency Rates</CardTitle>
                  <CardDescription>Live exchange rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">8</div>
                  <p className="text-sm text-gray-600">Currencies tracked</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="forex" className="space-y-6">
            <ForexManagement />
          </TabsContent>

          <TabsContent value="packages" className="space-y-6">
            <PackageManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}